import React, { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Platform, StyleSheet, View } from 'react-native';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Button,
  CustomNavigationHeader,
  Form,
  LabelText,
  theme,
} from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * The gift card form for the bag.
 */
export const BagGiftCardForm = (props: BagGiftCardFormProps) => {
  const { isLoading, onSubmit, onBack, onClose } = props;

  const { formatMessage } = useIntl();

  // ─── Form & Validation ────────────────────────────────────────────────────

  const validationSchema = useMemo(() => {
    const giftCardCodeValidation = yup
      .string()
      .required(formatMessage(messages.requiredGiftCardNumber));

    const registrationCodeValidation = yup
      .string()
      .required(formatMessage(messages.requiredRegistrationCode));

    return yup
      .object({
        giftCardCode: giftCardCodeValidation,
        registrationCode: registrationCodeValidation,
      })
      .required();
  }, [formatMessage]);

  const form = useForm<BagGiftCardFormData>({
    defaultValues: { giftCardCode: '', registrationCode: '' },
    mode: 'onBlur',
    resolver: yupResolver(validationSchema),
  });

  const { control, formState } = form;
  const { dirtyFields } = formState;
  const shouldDisableApplyCta = !(
    dirtyFields.giftCardCode && dirtyFields.registrationCode
  );

  // ──────────────────────────────────────────────────────────────────────────

  const submitForm = useCallback(() => {
    const { giftCardCode, registrationCode } = form.getValues();

    onSubmit(giftCardCode, registrationCode);
  }, [form, onSubmit]);

  return (
    <View style={styles.container}>
      <CustomNavigationHeader.Container safeAreaEdges={[]}>
        <CustomNavigationHeader.Button.Left
          name="IconCaretLeft"
          onPress={onBack}
        />

        <CustomNavigationHeader.Text>
          <FormattedMessage
            defaultMessage="Redeem gift card"
            description="Bag | Gift card form | Title"
          />
        </CustomNavigationHeader.Text>

        <CustomNavigationHeader.Button.Right
          name="IconClose"
          onPress={onClose}
        />
      </CustomNavigationHeader.Container>

      <View style={styles.content}>
        <View style={styles.fieldContainer}>
          <LabelText sizeMatch={['12']}>
            {formatMessage(messages.giftCardNumber)}
          </LabelText>

          <Form.TextFieldWithBackground
            name="giftCardCode"
            hideSubmitButton
            hideNoticeSpace
            style={styles.field}
            control={control}
            rules={{ required: true }}
            placeholder={formatMessage(messages.giftCardNumberPlaceholder)}
            accessibilityLabel={formatMessage(messages.giftCardNumber)}
            disabled={isLoading}
            onSubmit={submitForm}
          />
        </View>

        <View style={styles.fieldContainer}>
          <LabelText sizeMatch={['12']}>
            {formatMessage(messages.registrationCode)}
          </LabelText>

          <Form.TextFieldWithBackground
            name="registrationCode"
            hideSubmitButton
            hideNoticeSpace
            style={styles.field}
            control={control}
            rules={{ required: true }}
            placeholder={formatMessage(messages.registrationCodePlaceholder)}
            accessibilityLabel={formatMessage(messages.registrationCode)}
            disabled={isLoading}
            onSubmit={submitForm}
          />
        </View>
      </View>

      <Button
        size="large-wide"
        style={styles.cta}
        disabled={shouldDisableApplyCta}
        isLoading={isLoading}
        accessibilityLabel={formatMessage(messages.applyGiftCardCtaA11y)}
        onPress={submitForm}
      >
        <FormattedMessage {...messages.submitButtonLabel} />
      </Button>
    </View>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  giftCardNumber: {
    defaultMessage: 'Gift card number',
    description: 'Bag | Gift card | Gift card number',
  },
  registrationCode: {
    defaultMessage: 'Registration code',
    description: 'Bag | Gift card | Registration code',
  },
  giftCardNumberPlaceholder: {
    defaultMessage: 'Enter number',
    description: 'Bag | Gift card | Enter number',
  },
  registrationCodePlaceholder: {
    defaultMessage: 'Enter code',
    description: 'Bag | Gift card | Enter code',
  },
  applyGiftCardCtaA11y: {
    defaultMessage: 'Apply gift card',
    description: 'Bag | Gift card | CTA a11y',
  },
  submitButtonLabel: {
    defaultMessage: 'Redeem',
    description: 'Redeem Gift Card Form | Form submit button label',
  },
  requiredGiftCardNumber: {
    defaultMessage: 'Gift card number is required',
    description: 'Bag | Gift card | Required gift card number',
  },
  requiredRegistrationCode: {
    defaultMessage: 'Registration code is required',
    description: 'Bag | Gift card | Required gift card reg code',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    gap: theme.spacing['6'],
    paddingBottom: theme.spacing['4'],
  },
  content: {
    paddingHorizontal: theme.spacing['4'],
    gap: theme.spacing['4'],
  },
  fieldContainer: {
    flexGrow: 1,
    gap: theme.spacing['1'],
  },
  field: {
    paddingBottom: Platform.select({ android: theme.spacing['2'], default: 0 }),
  },
  cta: {
    alignSelf: 'center',
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type BagGiftCardFormProps = Readonly<{
  isLoading: boolean;
  onSubmit: (code: string, regCode: string) => void;
  onBack?: () => void;
  onClose?: () => void;
}>;

type BagGiftCardFormData = Readonly<{
  giftCardCode: string;
  registrationCode: string;
}>;
